import { create } from 'zustand';
import { messageOf } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import type { CourseModuleDto, CourseProgress, LessonDto } from '@/api/miniapp/types';

/**
 * Курс на реальном бэкенде.
 *
 * Модули приходят плоским списком с `course_id` (UUID) и порядком `order`;
 * уроки запрашиваются отдельно по модулю. Данные привязаны к активному
 * профилю, поэтому при его смене кэш сбрасывается — иначе на экране остался бы
 * курс прежнего профиля.
 */
interface BackendCourseState {
  modules: CourseModuleDto[];
  /** Уроки по идентификатору модуля: возврат к списку не должен грузить заново. */
  lessonsByCourse: Record<string, LessonDto[]>;
  /** Прогресс по каждому модулю приходит вместе с его уроками. */
  progressByCourse: Record<string, CourseProgress>;
  loadingModules: boolean;
  /**
   * Идёт загрузка уроков.
   *
   * Главный экран грузит уроки всех модулей разом, поэтому флага мало:
   * первый же завершившийся запрос снимал бы его, пока остальные ещё идут.
   * Считаются незавершённые запросы, а экрану отдаётся признак.
   */
  loadingLessons: boolean;
  error: string | null;

  loadModules(): Promise<void>;
  /** Прогресс всех модулей сразу: поле `status` у модуля этого не показывает. */
  loadAllProgress(): Promise<void>;
  loadLessons(courseId: string): Promise<void>;
  /** Сбросить кэш: после сдачи задания прогресс на сервере меняется. */
  invalidate(): void;
  reset(): void;
}

/** Незавершённые запросы уроков: главный экран грузит все модули разом. */
let pendingLessons = 0;

export const useBackendCourseStore = create<BackendCourseState>((set, get) => ({
  modules: [],
  lessonsByCourse: {},
  progressByCourse: {},
  loadingModules: false,
  loadingLessons: false,
  error: null,

  async loadModules() {
    // Кэш нужен только чтобы экран открывался мгновенно; данные всё равно
    // перечитываются. Прогресс меняется на сервере после сдачи задания, и
    // «сэкономленный» запрос оставлял бы на экране устаревшие цифры
    set({ loadingModules: true, error: null });
    try {
      const modules = await miniappApi.getModules();
      // Порядок задаёт сервер полем order, а не позиция в массиве
      set({
        modules: [...modules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        loadingModules: false,
      });
    } catch (error) {
      set({ loadingModules: false, error: messageOf(error, 'Не удалось загрузить программу') });
    }
  },

  async loadAllProgress() {
    const modules = get().modules;
    if (modules.length === 0) return;

    // Запросы идут параллельно: последовательные пять ожиданий заметны на телефоне
    await Promise.all(modules.map((module) => get().loadLessons(module.course_id)));
  },

  async loadLessons(courseId) {
    pendingLessons += 1;
    set({ loadingLessons: true, error: null });
    try {
      const response = await miniappApi.getLessons(courseId);
      pendingLessons -= 1;
      set((state) => ({
        // Порядок задаёт поле order, а не позиция в массиве
        lessonsByCourse: {
          ...state.lessonsByCourse,
          [courseId]: [...(response.lessons ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        },
        progressByCourse: { ...state.progressByCourse, [courseId]: response.progress },
        loadingLessons: pendingLessons > 0,
      }));
    } catch (error) {
      pendingLessons -= 1;
      set({
        loadingLessons: pendingLessons > 0,
        error: messageOf(error, 'Не удалось загрузить уроки'),
      });
    }
  },

  invalidate() {
    set({ lessonsByCourse: {}, progressByCourse: {} });
  },

  reset() {
    pendingLessons = 0;
    set({
      modules: [],
      lessonsByCourse: {},
      progressByCourse: {},
      loadingLessons: false,
      error: null,
    });
  },
}));
